import { useState, type ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { validateBlockId } from './block-id'

/**
 * Monospace slot for the `<mono>` tag in `blockConfigurator.fork.body`.
 *
 * A plain `<span className="font-mono" />` would work too, but react-i18next
 * clones a slot that wraps an interpolation with an internal
 * `i18nIsDynamicList` prop — React then warns about an unknown DOM attribute.
 * A component absorbs the extra prop silently.
 */
function Mono({ children }: { children?: ReactNode }) {
  return <span className="font-mono">{children}</span>
}

interface ForkBlockModalProps {
  sourceId: string
  sourceName: string
  sourceVersion: number
  existingIds: string[]
  listLoading?: boolean
  listFailed?: boolean
  onCancel: () => void
  onConfirm: (next: { id: string; name: string }) => void
}

export function ForkBlockModal({
  sourceId,
  sourceName,
  sourceVersion,
  existingIds,
  listLoading,
  listFailed,
  onCancel,
  onConfirm,
}: ForkBlockModalProps) {
  const { t } = useTranslation()
  const [id, setId] = useState(`${sourceId}-2`)
  const [name, setName] = useState(`${sourceName} (fork of v${sourceVersion})`)

  const idValidation = validateBlockId(id, existingIds)
  const nameNonEmpty = name.trim().length > 0
  const canConfirm = idValidation.ok && nameNonEmpty && !listLoading && !listFailed

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        data-testid="fork-modal"
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl dark:bg-stone-900"
      >
        <h3 className="mb-3 text-base font-semibold">{t('blockConfigurator.fork.title')}</h3>
        <p className="mb-3 text-xs text-stone-500">
          {/* <Trans> (not plain t()) because the source id is rendered
              monospace INSIDE the sentence — a locale is free to move the
              <mono> slot wherever its word order needs it. */}
          <Trans
            i18nKey="blockConfigurator.fork.body"
            values={{ id: sourceId, version: sourceVersion }}
            components={{ mono: <Mono /> }}
          />
        </p>
        {listLoading && (
          <p className="mb-3 text-[11px] text-stone-500">
            {t('blockConfigurator.fork.checkingIds')}
          </p>
        )}
        {listFailed && (
          <p className="mb-3 rounded border border-rose-300 bg-rose-50 px-2 py-1 text-[11px] text-rose-700 dark:border-rose-700 dark:bg-rose-950 dark:text-rose-300">
            {t('blockConfigurator.fork.listFailed')}
          </p>
        )}
        <div className="space-y-3">
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
              {t('blockConfigurator.fork.blockId')}
            </span>
            <input
              aria-label={t('blockConfigurator.fork.blockId')}
              value={id}
              onChange={e => setId(e.target.value)}
              className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 font-mono text-sm dark:border-stone-700 dark:bg-stone-950"
            />
            {!idValidation.ok && (
              <p className="mt-1 text-[11px] text-rose-600">
                {t(idValidation.error.key, idValidation.error.params)}
              </p>
            )}
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
              {t('blockConfigurator.fork.name')}
            </span>
            <input
              aria-label={t('blockConfigurator.fork.name')}
              value={name}
              onChange={e => setName(e.target.value)}
              className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-sm dark:border-stone-700 dark:bg-stone-950"
            />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-stone-300 px-3 py-1 text-sm text-stone-700 hover:bg-stone-100 dark:border-stone-700 dark:text-stone-300 dark:hover:bg-stone-800"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            data-testid="fork-modal-confirm"
            disabled={!canConfirm}
            onClick={() => onConfirm({ id, name })}
            className="rounded bg-sky-600 px-3 py-1 text-sm text-white disabled:opacity-40 hover:enabled:bg-sky-700"
          >
            {t('blockConfigurator.fork.confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}
